import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";
import {
  MODULO_COBROS,
  authConfig,
  badgeEstado,
  formatearFechaHora,
  formatearMoneda,
  manejarErrorSesion,
  nombreConcepto,
  nombreInstructor,
  obtenerModuloPorNombre,
  tienePermiso,
} from "../utils/cobros";

const ListarHistorialSuscripciones = () => {
  const navigate = useNavigate();
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);

  const modulo = useMemo(() => obtenerModuloPorNombre(MODULO_COBROS), []);
  const puedeLeer = useMemo(() => tienePermiso(modulo, "LEER"), [modulo]);

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setHistorial([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get("/api/historialsuscripciones/listall", authConfig());
      if (!Array.isArray(data?.historial)) throw new Error("Datos de historial no válidos.");
      setHistorial(data.historial);
    } catch (error) {
      console.error("Error cargando historial:", error);
      if (manejarErrorSesion(error, navigate)) return;
      Swal.fire("Error", error.response?.data?.message || "No se pudo cargar el historial.", "error");
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, puedeLeer]);

  useEffect(() => {
    getData();
  }, [getData]);

  const verDetalle = useCallback((registro) => {
    const instructor = registro.instructorObjectId;
    const pago = registro.pagoObjectId;
    const detalle = [
      `Instructor: ${nombreInstructor(instructor)}`,
      `Documento: ${instructor?.documentoInstructor || "—"}`,
      `Evento: ${String(registro.tipoEvento || "—").replaceAll("_", " ")}`,
      `Concepto pago: ${pago?.conceptoPago ? nombreConcepto(pago.conceptoPago) : "—"}`,
      `Origen evento: ${String(registro.origen || "—").replaceAll("_", " ")}`,
      `Estado anterior: ${registro.estadoAnterior || "—"}`,
      `Estado nuevo: ${registro.estadoNuevo || "—"}`,
      `Fecha: ${formatearFechaHora(registro.createdAt)}`,
      pago?.valorPago !== undefined ? `Valor pago: ${formatearMoneda(pago.valorPago, pago.moneda)}` : "",
      "",
      registro.descripcion || "Sin descripción.",
    ].filter(Boolean).join("\n");

    Swal.fire({
      title: "Detalle del historial",
      text: detalle,
      icon: "info",
      confirmButtonText: "Cerrar",
    });
  }, []);

  const dataTabla = useMemo(
    () => historial.map((registro) => {
      const instructor = registro.instructorObjectId;
      const pago = registro.pagoObjectId;
      return {
        ...registro,
        fechaFormateada: formatearFechaHora(registro.createdAt),
        documentoInstructor: instructor?.documentoInstructor || "—",
        instructorNombre: nombreInstructor(instructor),
        evento: String(registro.tipoEvento || "—").replaceAll("_", " "),
        concepto: pago?.conceptoPago ? nombreConcepto(pago.conceptoPago) : "—",
        estadoActual: registro.estadoNuevo ? badgeEstado(registro.estadoNuevo) : registro.estadoAnterior ? badgeEstado(registro.estadoAnterior) : "—",
        origenVisual: String(registro.origen || "—").replaceAll("_", " "),
        funciones: (
          <div className="d-flex justify-content-end">
            <button className="btn btn-outline-primary btn-sm" type="button" title="Ver detalle" onClick={() => verDetalle(registro)}>
              <i className="fa-solid fa-eye"></i>
            </button>
          </div>
        ),
      };
    }),
    [historial, verDetalle]
  );

  const columns = useMemo(() => [
    { Header: "Fecha", accessor: "fechaFormateada" },
    { Header: "Documento", accessor: "documentoInstructor" },
    { Header: "Instructor", accessor: "instructorNombre" },
    { Header: "Evento", accessor: "evento" },
    { Header: "Concepto", accessor: "concepto", hideHeaderOnMobile: true },
    { Header: "Estado actual", accessor: "estadoActual", hideHeaderOnMobile: true },
    { Header: "Origen", accessor: "origenVisual", hideHeaderOnMobile: true },
    { Header: "Detalle", accessor: "funciones" },
  ], []);

  if (!puedeLeer) {
    return <div className="container-fluid px-2 px-md-4"><div className="alert alert-warning">No tiene permisos para consultar el historial de suscripciones.</div></div>;
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
        <div className="px-3 px-md-4 py-3 border-bottom bg-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mt-2 mb-1 fw-semibold">Historial de suscripciones</h5>
              <small className="text-secondary">Auditoría automática de suscripciones, pagos, renovaciones, estados y anulaciones.</small>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-outline-dark" type="button" onClick={() => navigate("/inicio")}><i className="fa-solid fa-house me-2"></i>Inicio</button>
              <button className="btn btn-outline-secondary" type="button" onClick={getData} disabled={loading}><i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>Actualizar</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-secondary py-5"><div className="spinner-border" role="status"></div><div className="mt-3">Cargando historial…</div></div>
        ) : dataTabla.length > 0 ? (
          <div className="overflow-auto"><DataTable columns={columns} data={dataTabla} /></div>
        ) : (
          <div className="text-center text-secondary py-5"><div className="display-6 mb-2">📚</div><div className="fw-semibold">No hay eventos registrados</div></div>
        )}
      </div>
    </div>
  );
};

export default ListarHistorialSuscripciones;
